import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { endOfDay, format, startOfDay } from 'date-fns';

import { broadcastQueueApi } from '@/api/broadcastQueue';
import {
  countChannelChangeableQueueItems,
  getSelectedApproveableQueueIds,
  getSelectedChannelChangeableQueueIds,
  isBroadcastQueueRowSelectable,
} from '@/features/broadcast-queue/selectors';
import type {
  BulkChangeChannelReasonSummary,
  BulkChangeChannelResult,
  BroadcastMode,
  BroadcastOperationJob,
  BroadcastModeWarning,
  BroadcastQueueItem,
  BroadcastQueueStatus,
  BroadcastQueueSummary,
} from '@/types/broadcastQueue';
import { type MessagesWsEvent, useMessagesWebSocket } from '@/hooks/useMessagesWebSocket';

type ToastFn = (message: string) => void;

type UseBroadcastQueuePageArgs = {
  tenantId: string | null | undefined;
  showSuccess: ToastFn;
  showError: ToastFn;
  showWarning: ToastFn;
};

function isOperationJobTerminal(status?: string) {
  return status === 'completed' || status === 'failed' || status === 'partial';
}

// 24.03.26: вынесли state и action-flow страницы очереди.
// 10.04.26: approve-all переведён на async job с progress dialog, WS update и polling fallback.
export function useBroadcastQueuePage({
  tenantId,
  showSuccess,
  showError,
  showWarning,
}: UseBroadcastQueuePageArgs) {
  const [items, setItems] = useState<BroadcastQueueItem[]>([]);
  const [summary, setSummary] = useState<BroadcastQueueSummary | null>(null);
  const [mode, setMode] = useState<BroadcastMode>('manual');
  const [modeDescription, setModeDescription] = useState('');
  const [broadcastingEnabled, setBroadcastingEnabled] = useState(true);
  const [modeWarnings, setModeWarnings] = useState<BroadcastModeWarning[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createdFrom, setCreatedFrom] = useState<Date | null>(() => startOfDay(new Date()));
  const [createdTo, setCreatedTo] = useState<Date | null>(() => endOfDay(new Date()));

  const [confirmApproveAll, setConfirmApproveAll] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmToggleMode, setConfirmToggleMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const [previewItemId, setPreviewItemId] = useState<string | null>(null);

  const [bulkChannelSelect, setBulkChannelSelect] = useState('');
  const [showBulkChannelResult, setShowBulkChannelResult] = useState<{
    message: string;
    skippedMessage?: string;
    failureSummary?: string;
    failedItems?: BulkChangeChannelResult[];
    reasonSummary?: BulkChangeChannelReasonSummary[];
  } | null>(null);
  const [operationJob, setOperationJob] = useState<BroadcastOperationJob | null>(null);
  const completedOperationJobRef = useRef<string | null>(null);

  const dateFilters = useMemo(
    () => ({
      created_from: createdFrom ? format(createdFrom, 'yyyy-MM-dd') : undefined,
      created_to: createdTo ? format(createdTo, 'yyyy-MM-dd') : undefined,
      semantic_dates: true,
    }),
    [createdFrom, createdTo]
  );

  const selectedApproveableIds = useMemo(
    () => getSelectedApproveableQueueIds({ items, selectedIds }),
    [items, selectedIds]
  );

  const selectedPendingIds = useMemo(
    () =>
      selectedApproveableIds.filter((id) =>
        items.some((item) => item.id === id && item.status === 'pending_approval')
      ),
    [items, selectedApproveableIds]
  );

  const selectedChannelChangeableIds = useMemo(
    () => getSelectedChannelChangeableQueueIds({ items, selectedIds }),
    [items, selectedIds]
  );

  const pendingCount = summary?.pending_approval ?? 0;
  const operationJobId = operationJob?.id ?? null;
  const isOperationJobFinished = isOperationJobTerminal(operationJob?.status);

  const handleOperationJobEvent = useCallback((event: MessagesWsEvent) => {
    if (!operationJobId || event.type !== 'broadcast_approval_progress') {
      return;
    }
    const payload = event.payload as BroadcastOperationJob;
    if (payload.id !== operationJobId) {
      return;
    }
    setOperationJob(payload);
  }, [operationJobId]);

  const { status: operationJobWsStatus } = useMessagesWebSocket({
    tenantId: tenantId || undefined,
    enabled: !!tenantId && !!operationJobId && !isOperationJobFinished,
    onEvent: handleOperationJobEvent,
  });

  const channelChangeableCount = useMemo(
    () => countChannelChangeableQueueItems(items),
    [items]
  );

  const loadData = useCallback(async () => {
    if (!tenantId) {
      setItems([]);
      setTotal(0);
      setSummary(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [listRes, summaryRes, modeRes] = await Promise.all([
        broadcastQueueApi.list({
          tenant_id: tenantId,
          status: (statusFilter as BroadcastQueueStatus) || undefined,
          event_type: eventTypeFilter || undefined,
          phone: searchQuery || undefined,
          ...dateFilters,
          offset,
          limit,
        }),
        // 27.03.26: summary грузим с теми же фильтрами, что и список, чтобы UI не показывал разные цифры.
        broadcastQueueApi.summary(tenantId, {
          status: (statusFilter as BroadcastQueueStatus) || undefined,
          event_type: eventTypeFilter || undefined,
          phone: searchQuery || undefined,
          ...dateFilters,
        }),
        broadcastQueueApi.getMode(tenantId),
      ]);

      setItems(listRes.items || []);
      setTotal(listRes.total || 0);
      setSummary(summaryRes);
      setMode(modeRes.mode);
      setModeDescription(modeRes.description);
      setBroadcastingEnabled(modeRes.broadcasting_enabled ?? true);
      setModeWarnings(modeRes.warnings ?? []);
    } catch {
      showError('Не удалось загрузить очередь рассылок');
    } finally {
      setLoading(false);
    }
  }, [
    dateFilters,
    eventTypeFilter,
    limit,
    offset,
    searchQuery,
    showError,
    statusFilter,
    tenantId,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!tenantId || !operationJobId || isOperationJobFinished) {
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const nextJob = await broadcastQueueApi.getOperationJob(operationJobId, tenantId || undefined);
        if (!cancelled) {
          setOperationJob(nextJob);
        }
      } catch {
        // Фоновый polling молчит, чтобы не зашумлять UI transient-ошибками.
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, operationJobWsStatus === 'connected' ? 5000 : 2000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isOperationJobFinished, operationJobId, operationJobWsStatus, tenantId]);

  useEffect(() => {
    if (!operationJob || !isOperationJobTerminal(operationJob.status)) {
      return;
    }
    if (completedOperationJobRef.current === operationJob.id) {
      return;
    }

    completedOperationJobRef.current = operationJob.id;
    setSelectedIds(new Set());
    void loadData();

    if (operationJob.status === 'completed') {
      showSuccess(operationJob.message || 'Массовое одобрение завершено');
      return;
    }
    if (operationJob.status === 'partial') {
      showWarning(operationJob.message || 'Массовое одобрение завершено с ошибками');
      return;
    }
    showError(operationJob.message || operationJob.last_error || 'Массовое одобрение завершилось ошибкой');
  }, [loadData, operationJob, showError, showSuccess, showWarning]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, eventTypeFilter, searchQuery, createdFrom, createdTo]);

  const handleLimitChange = (nextLimit: number) => {
    setOffset(0);
    setLimit(nextLimit);
  };

  const handleApprove = async () => {
    // 10.04.26: точечное одобрение выбранных строк пока sync,
    // чтобы не менять отдельно UX single/selective approve в этом rollout.
    if (selectedApproveableIds.length === 0) {
      showWarning('Для одобрения выберите сообщения со статусом «Ожидает одобрения» или «Ошибка»');
      return;
    }

    try {
      setActionLoading(true);
      if (selectedApproveableIds.length !== selectedIds.size) {
        showWarning('Будут одобрены только сообщения со статусом «Ожидает одобрения» или «Ошибка»');
      }
      const result = await broadcastQueueApi.approve(selectedApproveableIds, tenantId || undefined);
      showSuccess(result.message || `Одобрено: ${result.approved}`);
      setSelectedIds(new Set());
      await loadData();
    } catch {
      showError('Ошибка при одобрении');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAll = async () => {
    // 10.04.26: bulk approve уже async.
    // UI сразу открывает progress dialog, а сам send идёт в фоне через job + WS/polling.
    try {
      setActionLoading(true);
      const result = await broadcastQueueApi.approveAll(tenantId || undefined);
      setOperationJob(result.job);
      setSelectedIds(new Set());
      setConfirmApproveAll(false);
      if (result.message.toLowerCase().includes('уже выполняется')) {
        showWarning(result.message);
      } else {
        showSuccess(result.message);
      }
    } catch {
      showError('Ошибка при массовом одобрении');
    } finally {
      setActionLoading(false);
    }
  };

  const closeOperationJob = useCallback(() => {
    if (operationJob && !isOperationJobTerminal(operationJob.status)) {
      return;
    }
    setOperationJob(null);
  }, [operationJob]);

  const handleReject = async () => {
    if (selectedPendingIds.length === 0) {
      showWarning('Для отклонения выберите сообщения со статусом «Ожидает одобрения»');
      return;
    }

    try {
      setActionLoading(true);
      if (selectedPendingIds.length !== selectedIds.size) {
        showWarning('Будут отклонены только сообщения со статусом «Ожидает одобрения»');
      }
      const result = await broadcastQueueApi.reject(selectedPendingIds, rejectReason || undefined);
      showSuccess(result.message || `Отклонено: ${result.rejected}`);
      setSelectedIds(new Set());
      setShowRejectDialog(false);
      setRejectReason('');
      await loadData();
    } catch {
      showError('Ошибка при отклонении');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClear = async () => {
    try {
      setActionLoading(true);
      const result = await broadcastQueueApi.clear(tenantId || undefined);
      showSuccess(result.message || `Очищено: ${result.cleared}`);
      setSelectedIds(new Set());
      setConfirmClear(false);
      await loadData();
    } catch {
      showError('Ошибка при очистке');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleMode = async () => {
    const newMode = mode === 'manual' ? 'auto' : 'manual';
    try {
      setActionLoading(true);
      const result = await broadcastQueueApi.setMode(newMode, tenantId || undefined);
      setMode(result.mode);
      setModeDescription(result.description);
      showSuccess(result.message);
      setConfirmToggleMode(false);
      await loadData();
    } catch {
      showError('Ошибка при переключении режима');
    } finally {
      setActionLoading(false);
    }
  };

  const selectAllPending = () => {
    setSelectedIds(new Set(items.filter((item) => item.status === 'pending_approval').map((item) => item.id)));
  };

  const selectAllChannelChangeable = () => {
    setSelectedIds(new Set(items.filter(isBroadcastQueueRowSelectable).map((item) => item.id)));
  };

  const handleBulkChannelChange = async () => {
    if (selectedChannelChangeableIds.length === 0) {
      showWarning('Выберите сообщения со статусом «Ожидает одобрения», «Одобрено», «Отправлено» или «Ошибка»');
      return;
    }
    if (!bulkChannelSelect) {
      showWarning('Выберите канал');
      return;
    }

    try {
      setActionLoading(true);
      const result = await broadcastQueueApi.bulkChangeChannel(
        selectedChannelChangeableIds,
        bulkChannelSelect,
        tenantId || undefined
      );
      if (result.failed > 0) {
        setShowBulkChannelResult({
          message: result.message,
          skippedMessage: result.skipped_message,
          failureSummary: result.failure_summary,
          failedItems: result.failed_items,
          reasonSummary: result.reason_summary,
        });
        showWarning(result.message);
      } else {
        showSuccess(result.message);
      }
      // 29.03.26: после bulk-смены канала сохраняем выбранные чекбоксы,
      // чтобы оператор мог сразу одобрить те же сообщения без повторного выбора.
      setBulkChannelSelect('');
      await loadData();
    } catch {
      showError('Ошибка при смене канала');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    items,
    summary,
    mode,
    modeDescription,
    broadcastingEnabled,
    modeWarnings,
    total,
    offset,
    setOffset,
    limit,
    loading,
    actionLoading,
    selectedIds,
    setSelectedIds,
    statusFilter,
    setStatusFilter,
    eventTypeFilter,
    setEventTypeFilter,
    searchQuery,
    setSearchQuery,
    createdFrom,
    setCreatedFrom,
    createdTo,
    setCreatedTo,
    confirmApproveAll,
    setConfirmApproveAll,
    confirmClear,
    setConfirmClear,
    confirmToggleMode,
    setConfirmToggleMode,
    rejectReason,
    setRejectReason,
    showRejectDialog,
    setShowRejectDialog,
    previewItemId,
    setPreviewItemId,
    bulkChannelSelect,
    setBulkChannelSelect,
    showBulkChannelResult,
    setShowBulkChannelResult,
    operationJob,
    closeOperationJob,
    isOperationJobWsConnected: operationJobWsStatus === 'connected',
    selectedApproveableIds,
    selectedPendingIds,
    selectedChannelChangeableIds,
    pendingCount,
    channelChangeableCount,
    loadData,
    handleLimitChange,
    handleApprove,
    handleApproveAll,
    handleReject,
    handleClear,
    handleToggleMode,
    handleBulkChannelChange,
    selectAllPending,
    selectAllChannelChangeable,
  };
}
